import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Portal,
  SxProps,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState, CSSProperties, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import defaultImage from '@/assets/avatar.png';
import { ApiProvider } from '@/provider/ApiProvider';
import Box from '@mui/system/Box';
import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import { GlobalSearchQueryDTO } from '@/@dtos/Queries/GlobalSearchQueryDTO';
import { ResponseDTO } from '@/@dtos/ResponseDTO';
import escapeRegExp from '@/utils/escapeRegExp';

const highlightText = (text: string, searchTerm: string) => {
  if (!searchTerm) return text;

  const escapedSearchTerm = escapeRegExp(searchTerm);
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) =>
    part.toLowerCase() === searchTerm.toLowerCase() ? (
      <strong key={index}>{part}</strong>
    ) : (
      part
    ),
  );
};


const searchResultRowSx = {
  backgroundColor: '#ffffff',
  border: '1px solid #dce3ef',
  borderRadius: '12px',
  padding: '12px 14px',
  marginBottom: '8px',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  boxShadow: '0 10px 24px -22px rgba(15, 23, 42, 0.38)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 16px 30px -24px rgba(15, 23, 42, 0.45)',
  },
} as const;

const searchResultCategorySx = {
  fontSize: '12px',
  color: '#64748b',
  marginBottom: '4px',
  fontWeight: 600,
} as const;

const searchResultBodySx = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
} as const;

const searchResultAvatarSx = {
  width: 40,
  height: 40,
} as const;

const searchResultTextSx = {
  display: 'flex',
  flexDirection: 'column',
} as const;

const searchResultTitleSx = {
  fontSize: '15px',
  fontWeight: 600,
  margin: 0,
  color: '#0f172a',
} as const;

const searchResultMetaSx = {
  fontSize: '12px',
  color: '#64748b',
  marginTop: '2px',
} as const;

type GlobalItemProps = {
  id: number;
  name: string;
  logo: string | null;
  metadata: string[];
  isActive: boolean;
  status: string | null;
}[];

type GlobalSearchState = {
  properties: GlobalItemProps;
  brands: GlobalItemProps;
  activations: GlobalItemProps;
  contracts: GlobalItemProps;
  campaigns: GlobalItemProps;
  totals?: {
    properties: number;
    brands: number;
    activations: number;
    contracts: number;
    campaigns: number;
  };
  has_more?: {
    properties: boolean;
    brands: boolean;
    activations: boolean;
    contracts: boolean;
    campaigns: boolean;
  };
};

type Props = {
  ContainerStyles?: SxProps;
  InputStyles?: SxProps;
  ScrollbarsStyles?: CSSProperties;
  size?: 'small' | 'medium';
  placeholder?: string | undefined;
  label?: string | null;
  iconPosition?: 'start' | 'end';
  variant?: 'default' | 'header';
};

export const GlobalSearchBar = ({
  ContainerStyles = {},
  InputStyles = {},
  ScrollbarsStyles = {},
  size = 'small',
  placeholder = undefined,
  label = null,
  iconPosition = 'end',
  variant = 'default',
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { sidebarStyle } = useSidebarNavigation();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const isHeaderVariant = variant === 'header';
  const {
    width: _,
    maxWidth: __,
    position: _pos,
    top: _top,
    left: _left,
    zIndex: _zi,
    height: _h,
    ...rawPanelCustomStyles
  } = ScrollbarsStyles;
  const panelCustomStyles = isHeaderVariant ? rawPanelCustomStyles : {};

  const [searchBarWidth, setSearchBarWidth] = useState(0);
  const [searchResultPanelWidth, setSearchResultPanelWidth] = useState(0);
  const [panelAnchor, setPanelAnchor] = useState({ top: 0, left: 0 });
  const searchBarRef = useRef<HTMLDivElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);

  const [searchResults, setSearchResults] = useState<GlobalSearchState>({
    properties: [],
    brands: [],
    activations: [],
    contracts: [],
    campaigns: [],
    totals: {
      properties: 0,
      brands: 0,
      activations: 0,
      contracts: 0,
      campaigns: 0,
    },
    has_more: {
      properties: false,
      brands: false,
      activations: false,
      contracts: false,
      campaigns: false,
    },
  });

  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const hasResults = Object.entries(searchResults)
    .filter(([key]) => key !== 'totals') // ignore totals
    .some(([, arr]) => (arr as GlobalItemProps).length > 0);

  // Get all results (first 3 items from each category)
  const getBestResults = () => {
    const allResults: Array<GlobalItemProps[0] & { category: string; linkPrefix: string }> = [];

    const categories = [
      { key: 'properties', title: 'Right Holder/Partner', linkPrefix: '/partnerProfile' },
      { key: 'brands', title: 'Brand', linkPrefix: '/brandprofile' },
      { key: 'contracts', title: 'Contract', linkPrefix: '/viewcontract' },
      { key: 'activations', title: 'Activity', linkPrefix: '/viewActivity' },
      { key: 'campaigns', title: 'Activation', linkPrefix: '/viewActivation' },
    ] as const;

    categories.forEach(({ key, title, linkPrefix }) => {
      const categoryResults = searchResults[key] as GlobalItemProps;
      categoryResults.forEach((result) => {
        allResults.push({
          ...result,
          category: title,
          linkPrefix,
        });
      });
    });

    return allResults;
  };

  const bestResults = getBestResults();

  const tabs = [
    {
      key: 'best',
      title: 'Best Results',
      linkPrefix: '',
      data: bestResults,
      total: bestResults.length,
    },
    {
      key: 'properties',
      title: 'RH/Partner',
      linkPrefix: '/partnerProfile',
      data: searchResults.properties.slice(0, 5), // Show only first 5
      total: searchResults.totals?.properties || searchResults.properties.length,
    },
    {
      key: 'brands',
      title: 'Brand',
      linkPrefix: '/brandprofile',
      data: searchResults.brands.slice(0, 5),
      total: searchResults.totals?.brands || searchResults.brands.length,
    },
    {
      key: 'contracts',
      title: 'Contract',
      linkPrefix: '/viewcontract',
      data: searchResults.contracts.slice(0, 5),
      total: searchResults.totals?.contracts || searchResults.contracts.length,
    },
    {
      key: 'activations',
      title: 'Activity',
      linkPrefix: '/viewActivity',
      data: searchResults.activations.slice(0, 5),
      total: searchResults.totals?.activations || searchResults.activations.length,
    },
    {
      key: 'campaigns',
      title: 'Activation',
      linkPrefix: '/viewActivation',
      data: searchResults.campaigns.slice(0, 5),
      total: searchResults.totals?.campaigns || searchResults.campaigns.length,
    },
  ] as const;

  const getGlobalSearchData = async (value: string, controller: AbortController) => {
    setShowSearchResults(true);
    setIsLoading(true);

    const body = new FormData();
    body.append('term', value);

    try {
      const response = (await ApiProvider.webservicePostToken(
        'Search/GlobalSearch',
        body,
        'yes',
        controller.signal,
      )) as ResponseDTO<GlobalSearchQueryDTO>;

      if (!response.success) throw new Error(response?.message);

      const { properties, brands, activations, contracts, campaigns } = response.data;

      const propertiesData = properties.map((property) => ({
        id: property.id,
        name: property.text,
        logo: property.logo,
        metadata: [
          typeof property.country === 'object' && property.country !== null
            ? property.country.name
            : property.country,
          property.property_type,
          property.added_as,
        ],
        isActive: property.is_active === 1,
        status: property.status,
      }));

      const brandsData = brands.map((brand) => ({
        id: brand.id,
        name: brand.text,
        logo: brand.logo,
        metadata: [brand.brand_industry?.[0]?.name, brand.added_as],
        isActive: brand.is_active === 1,
        status: brand.status,
      }));

      const activitiesData = activations.map((activity) => ({
        id: activity.id,
        name: activity.text,
        logo: null,
        metadata: [
          typeof activity.country === 'object' && activity.country !== null
            ? activity.country.name
            : activity.country,
        ],
        isActive: activity.is_active === 1,
        status: activity.status,
      }));

      const contractsData = contracts.map((contract) => {
        const contractProperties = Array.isArray(contract.properties)
          ? contract.properties.map((property) => property.name).join(', ')
          : '';

        const country =
          typeof contract.country === 'object' && contract.country !== null
            ? contract.country.name
            : contract.country;

        return {
          id: contract.id,
          name: contract.text,
          logo: null,
          metadata: [country, contractProperties],
          isActive: contract.is_active === 1,
          status: contract.status,
        };
      });

      const activationsData = campaigns.map((campaign) => ({
        id: campaign.id,
        name: campaign.text,
        logo: null,
        metadata: [
          typeof campaign.country === 'object' && campaign.country !== null
            ? campaign.country.name
            : campaign.country,
        ],
        isActive: campaign.is_active === 1,
        status: campaign.status,
      }));

      setSearchResults({
        properties: propertiesData,
        brands: brandsData,
        activations: activitiesData,
        contracts: contractsData,
        campaigns: activationsData,
        totals: response.data.totals || {
          properties: propertiesData.length,
          brands: brandsData.length,
          activations: activitiesData.length,
          contracts: contractsData.length,
          campaigns: activationsData.length,
        },
        has_more: response.data.has_more || {
          properties: false,
          brands: false,
          activations: false,
          contracts: false,
          campaigns: false,
        },
      });
    } catch (error: any) {
      let message = 'Something went wrong, please try again later.';
      if (error?.message) {
        const newMessage: string = error.message;
        if (newMessage.includes('aborted')) return;
        message = error.message;
      }
      ApiProvider.globalToastMessage(message, 5000, 'error');
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyUp = (event: any) => {
    const value: string = event.target.value;

    if (!value.length) closeGlobalSearch();

    setSearchQuery(value ?? '');
  };

  function closeGlobalSearch() {
    setShowSearchResults(false);
    setIsLoading(false);
    setSearchResults({
      properties: [],
      brands: [],
      activations: [],
      contracts: [],
      campaigns: [],
    });
    setTabIndex(0);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideSearchBar = searchContainerRef.current?.contains(target);
      const insidePanel = searchPanelRef.current?.contains(target);
      if (!insideSearchBar && !insidePanel) {
        setShowSearchResults(false);
      }
    };

    const updateWidth = () => {
      const inputWidth = searchBarRef.current?.offsetWidth ?? 0;
      const containerRect = searchContainerRef.current?.getBoundingClientRect();
      const containerLeft = containerRect?.left ?? 0;
      const containerBottom = containerRect?.bottom ?? 0;
      const viewportWidth = window.innerWidth;

      const availablePanelWidth = Math.max(viewportWidth - containerLeft - 16, 0);
      const preferredPanelWidth = isHeaderVariant
        ? viewportWidth < 900
          ? viewportWidth - 24
          : Math.max(inputWidth, Math.round(viewportWidth * 0.62))
        : inputWidth;
      const resolvedPanelWidth = Math.min(
        Math.max(inputWidth, preferredPanelWidth),
        availablePanelWidth > 0 ? availablePanelWidth : inputWidth,
      );

      setSearchBarWidth(inputWidth);
      setSearchResultPanelWidth(resolvedPanelWidth);
      setPanelAnchor({ top: containerBottom + 2, left: containerLeft });
    };

    window.addEventListener('resize', updateWidth);
    window.addEventListener('scroll', updateWidth, true);
    document.addEventListener('mousedown', handleClickOutside);
    updateWidth(); // Initial width set

    return () => {
      window.removeEventListener('resize', updateWidth);
      window.removeEventListener('scroll', updateWidth, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHeaderVariant]);

  useEffect(() => {
    if (showSearchResults && searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      setPanelAnchor({ top: rect.bottom + 2, left: rect.left });
    }
  }, [showSearchResults]);

  useEffect(() => {
    const controller = new AbortController();

    if (!searchQuery.length) {
      closeGlobalSearch();
      return;
    }
    setShowSearchResults(true);
    setIsLoading(true);

    const queryTimeout = setTimeout(() => {
      getGlobalSearchData(searchQuery, controller);
    }, 1000);

    return () => {
      clearTimeout(queryTimeout);
      controller.abort();
    };
  }, [searchQuery]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <div ref={searchContainerRef} style={{ position: 'relative', flex: 1 }}>
      <Box
        ref={searchBarRef}
        sx={{
          position: 'relative',
          zIndex: '9',
          flex: 1,
          height: sidebarStyle === 'full' ? undefined : '40px',
          ...ContainerStyles,
        }}
      >
        {/* ADD inputRef TO THE TEXTFIELD */}
        <TextField
          onKeyUp={handleKeyUp}
          label={label}
          placeholder={placeholder}
          variant='outlined'
          size={size}
          onFocus={() => setShowSearchResults(true)}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#ffffff',
            },
            ...InputStyles,
            ...(sidebarStyle === 'mini'
              ? {
                transition: 'width .3s, backgroundColor .3s',
                borderColor: 'green',
                borderWidth: '2px',
              }
              : {}),
          }}
          InputProps={{
            [`${iconPosition}Adornment`]: (
              <InputAdornment position={iconPosition}>
                <IconButton color='secondary' size='large'>
                  <SearchIcon color='primary' />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {showSearchResults && !!searchQuery && (
        <Portal>
          <div
            ref={searchPanelRef}
            style={{
              position: 'fixed',
              top: panelAnchor.top,
              left: panelAnchor.left,
              zIndex: 1400,
              width: searchResultPanelWidth || searchBarWidth,
              maxHeight: 'calc(100vh - 60px)',
              overflowY: 'auto',
              ...panelCustomStyles,
            }}
          >
            <Card
              variant='outlined'
              sx={{
                borderColor: '#dce3ef',
                borderRadius: '12px',
                boxShadow: '0 18px 36px -26px rgba(15, 23, 42, 0.5)',
              }}
            >
              <CardContent
                className={isHeaderVariant ? undefined : 'card_content box_shadow'}
                sx={{
                  padding: '16px !important',
                  ...(isHeaderVariant ? { minHeight: '15.313rem' } : {}),
                }}
              >
                {isLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant='h4' color='primary' style={{ marginTop: 24 }}>
                      Loading search results
                    </Typography>
                    <CircularProgress color='primary' size={35} style={{ marginTop: 24 }} />
                  </div>
                ) : !hasResults ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant='h4' color='primary' style={{ marginTop: 24 }}>
                      No Results Found
                    </Typography>
                  </div>
                ) : (
                  <>
                    <Tabs
                      value={tabIndex}
                      onChange={handleTabChange}
                      variant='scrollable'
                      scrollButtons='auto'
                      aria-label='Global Search Tabs'
                      sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                          color: '#64748b',
                          borderBottom: '2px solid #cbd5e1',
                          textTransform: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          minWidth: 'auto',
                          padding: '6px 12px',
                          '&.Mui-selected': {
                            color: '#2563eb',
                            borderBottom: '2px solid transparent',
                          },
                          '&:hover': {
                            color: '#2563eb',
                          },
                        },
                        '& .MuiTabs-flexContainer': {
                          borderBottom: '1px solid #cbd5e1',
                        },
                      }}
                    >
                      {tabs.map(({ key, title, data, total }) => (
                        <Tab
                          key={key}
                          label={`${title} (${key === 'best' ? data.length : total || data.length})`}
                          disabled={data.length === 0}
                          wrapped
                        />
                      ))}
                    </Tabs>
                    <Box sx={{ paddingLeft: '12px' }}>
                      {tabs.map(({ key, title, linkPrefix, data, total }, index) => (
                        <TabPanel key={key} value={tabIndex} index={index}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2,
                              paddingBottom: '8px',
                            }}
                          >
                            <Typography
                              variant='body2'
                              sx={{
                                color: '#0f172a',
                                fontWeight: 500,
                              }}
                            >
                              {key === 'best' ? (
                                <>
                                  Best results for <strong>"{searchQuery}"</strong> ({data.length})
                                </>
                              ) : (
                                <>
                                  {/* checking the est total for just brands and property */}
                                  {title} results for <strong>"{searchQuery}"</strong> (
                                  {Math.min(data.length, 5)} of {total}
                                  {(key === 'properties' || key === 'brands') &&
                                    searchResults.has_more?.[key] &&
                                    '+'}{' '}
                                  results)
                                </>
                              )}
                            </Typography>

                            {key !== 'best' && (total || data.length) > 5 && (
                              <React.Fragment>
                                {Boolean(
                                  ApiProvider.permission(
                                    {
                                      properties: 'property_list_view',
                                      brands: 'brand_list_view',
                                      activations: 'activation_list_view',
                                      contracts: 'contract_list_view',
                                      campaigns: 'campaign_list_view',
                                    }[key] || '',
                                  ),
                                ) && (
                                    <Typography
                                      component='a'
                                      href={`${window.location.origin}/#/listview/${key === 'properties' ? 'property' : key === 'brands' ? 'brand' : key === 'contracts' ? 'contract' : key === 'activations' ? 'activation' : 'activation'}?search=${encodeURIComponent(searchQuery)}`}
                                      sx={{
                                        color: '#2563eb',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          color: '#1d4ed8',
                                          fontWeight: 600,
                                        },
                                      }}
                                    >
                                      See All
                                    </Typography>
                                  )}
                              </React.Fragment>
                            )}
                          </Box>

                          {key === 'best' ? (
                            <RenderBestResultsSection
                              data={bestResults}
                              searchQuery={searchQuery}
                              useInlineStyles={isHeaderVariant}
                              onResultClick={closeGlobalSearch}
                            />
                          ) : (
                            <RenderSection
                              title={title}
                              data={data}
                              linkPrefix={linkPrefix}
                              searchQuery={searchQuery}
                              useInlineStyles={isHeaderVariant}
                              onResultClick={closeGlobalSearch}
                            />
                          )}
                        </TabPanel>
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </Portal>
      )}
    </div>
  );
};

type RenderSectionProps = {
  title: string;
  data: GlobalItemProps;
  linkPrefix: string;
  useInlineStyles?: boolean;
  onResultClick: () => void;
};

function RenderSection({
  title,
  data,
  linkPrefix,
  searchQuery,
  useInlineStyles = false,
  onResultClick,
}: RenderSectionProps & { searchQuery: string }) {
  const navigate = useNavigate();
  function navigateToLink(id: number) {
    onResultClick();
    navigate(`${linkPrefix}/${id}`);
  }

  return (
    <Grid
      size={{
        lg: 12,
      }}
    >
      {data.map((row) =>
        useInlineStyles ? (
          <Box key={row.id} onClick={() => navigateToLink(row.id)} sx={searchResultRowSx}>
            <Typography sx={searchResultCategorySx}>{title}</Typography>

            <Box sx={searchResultBodySx}>
              <Avatar src={row.logo ?? defaultImage} sx={searchResultAvatarSx} />
              <Box sx={searchResultTextSx}>
                <Typography sx={searchResultTitleSx}>
                  {highlightText(row.name, searchQuery)}
                </Typography>
                <Typography sx={searchResultMetaSx}>
                  {[...row.metadata, row.status || 'Inactive'].filter(Boolean).join(' | ')}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <div key={row.id} onClick={() => navigateToLink(row.id)} className='search-result-row'>
            <Typography className='category-above-list-name'>{title}</Typography>

            <div className='row-body'>
              <Avatar src={row.logo ?? defaultImage} className='card-avatar' />
              <div className='card-text'>
                <Typography className='list-title'>
                  {highlightText(row.name, searchQuery)}
                </Typography>
                <Typography className='list-meta'>
                  {[...row.metadata, row.status || 'Inactive'].filter(Boolean).join(' | ')}
                </Typography>
              </div>
            </div>
          </div>
        ),
      )}
    </Grid>
  );
}

type BestResultItem = GlobalItemProps[0] & { category: string; linkPrefix: string };

function RenderBestResultsSection({
  data,
  searchQuery,
  useInlineStyles = false,
  onResultClick,
}: {
  data: BestResultItem[];
  searchQuery: string;
  useInlineStyles?: boolean;
  onResultClick: () => void;
}) {
  const navigate = useNavigate();
  function navigateToLink(id: number, linkPrefix: string) {
    onResultClick();
    navigate(`${linkPrefix}/${id}`);
  }

  return (
    <Grid
      size={{
        lg: 12,
      }}
    >
      {useInlineStyles ? (
        <Box sx={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
          {data.map((row) => (
            <Box
              key={`${row.category}-${row.id}`}
              onClick={() => navigateToLink(row.id, row.linkPrefix)}
              sx={searchResultRowSx}
            >
              <Typography sx={searchResultCategorySx}>{row.category}</Typography>

              <Box sx={searchResultBodySx}>
                <Avatar src={row.logo ?? defaultImage} sx={searchResultAvatarSx} />
                <Box sx={searchResultTextSx}>
                  <Typography sx={searchResultTitleSx}>
                    {highlightText(row.name, searchQuery)}
                  </Typography>
                  <Typography sx={searchResultMetaSx}>
                    {[...row.metadata, row.status || 'Inactive'].filter(Boolean).join(' | ')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
          {data.map((row) => (
            <div
              key={`${row.category}-${row.id}`}
              onClick={() => navigateToLink(row.id, row.linkPrefix)}
              className='search-result-row'
            >
              <Typography className='category-above-list-name'>{row.category}</Typography>

              <div className='row-body'>
                <Avatar src={row.logo ?? defaultImage} className='card-avatar' />
                <div className='card-text'>
                  <Typography className='list-title'>
                    {highlightText(row.name, searchQuery)}
                  </Typography>
                  <Typography className='list-meta'>
                    {[...row.metadata, row.status || 'Inactive'].filter(Boolean).join(' | ')}
                  </Typography>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Grid>
  );
}

// Helper TabPanel component for accessibility and display
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`global-search-tabpanel-${index}`}
      aria-labelledby={`global-search-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}
